import { dbOps } from "../hooks/dbOps.store";
import { Intents } from "@certa/studio-core/src/constants/activityLogs.constants";
import type {
  DataObjectField,
  DataObjectFieldWidth,
  DataObjectType,
  DataObjectUISchemaField,
  DataObjectFieldsSchema,
  DataObjectUISchemaSection,
  DataObjectUISchemaFieldTypes
} from "@certa/types/src/dataObjects.types";

const getDefaultSections = (
  objectSchemaFields: DataObjectFieldsSchema["properties"]
): DataObjectUISchemaSection[] => [
  {
    title: "Overview",
    fields: Object.keys(objectSchemaFields ?? {}).map(fieldTag => {
      return { width: "1/2", tag: fieldTag };
    })
  }
];

export const addDataObject = (dataObject: DataObjectType) => {
  dbOps().transaction(
    () => {
      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.ADD_DATA_OBJECT
    }
  );
};

export const importDataObject = (dataObject: DataObjectType) => {
  dbOps().transaction(
    () => {
      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type UpdateNameAndDescriptionParams = {
  dataObjectTag: string;
  name: string;
  pluralName?: string;
  description: string;
};

export const updateNameAndDescription = ({
  dataObjectTag,
  name,
  pluralName,
  description
}: UpdateNameAndDescriptionParams) => {
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (dataObject) {
        const displayNames = dataObject.uiSchema.displayNames;
        if (displayNames) {
          displayNames.singular = name;
          displayNames.plural = pluralName ?? name;
        } else {
          dataObject.uiSchema.displayNames = {
            singular: name,
            plural: name
          };
        }
        dataObject.__description = description;
        dbOps().setDataObject(dataObject.tag, dataObject);
      }
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type AddFieldToDataObjectParams = {
  dataObjectTag: string;
  sectionIndex: number;
  fieldTag: string;
  field: DataObjectField;
  fieldUISchema: DataObjectUISchemaField;
};

export const addFieldToDataObject = (params: AddFieldToDataObjectParams) => {
  const { dataObjectTag, sectionIndex, fieldTag, field, fieldUISchema } =
    params;
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (dataObject) {
        // update schema
        dataObject.schema.properties[fieldTag] = field;

        // update uiSchema fields
        if (dataObject.uiSchema.fields) {
          dataObject.uiSchema.fields[fieldTag] = fieldUISchema;
        } else {
          dataObject.uiSchema.fields = {
            [fieldTag]: fieldUISchema
          };
        }

        // update uiSchema sections
        const sections = dataObject.uiSchema?.detail?.sections;
        if (sections && sections.length > 0) {
          const section = sections[sectionIndex];
          if (section) {
            section.fields.push({
              tag: fieldTag,
              width: "1/2"
            });
          } else {
            // eslint-disable-next-line no-console
            console.error(JSON.stringify(dataObject, null, 2));
            throw new Error(
              `Section ${sectionIndex} not found in record ${dataObjectTag} while adding field ${fieldTag}`
            );
          }
        } else {
          dataObject.uiSchema = {
            ...dataObject.uiSchema,
            detail: {
              sections: getDefaultSections(dataObject.schema.properties)
            }
          };
        }
        dbOps().setDataObject(dataObject.tag, dataObject);
      }
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type AddSectionToDataObjectParams = {
  dataObjectTag: string;
};

export const addSectionToDataObject = (
  params: AddSectionToDataObjectParams
) => {
  const { dataObjectTag } = params;
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (dataObject) {
        const sections = dataObject.uiSchema.detail?.sections;
        if (sections && sections.length > 0) {
          sections.push({
            title: "Untitled Section",
            fields: []
          });
        } else {
          const sections = getDefaultSections(dataObject.schema.properties);
          sections.push({
            title: "Untitled Section",
            fields: []
          });
          dataObject.uiSchema.detail = {
            ...dataObject.uiSchema.detail,
            sections
          };
        }
        dbOps().setDataObject(dataObject.tag, dataObject);
      } else {
        throw new Error(`Record ${dataObjectTag} not found`);
      }
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};
type UpdateFieldWidthParams = {
  dataObjectTag: string;
  sectionIndex: number;
  fieldTag: string;
  fieldWidth: DataObjectFieldWidth;
};

export const updateFieldWidth = (params: UpdateFieldWidthParams) => {
  const { dataObjectTag, sectionIndex, fieldTag, fieldWidth } = params;
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);

      if (dataObject) {
        const sections = dataObject.uiSchema.detail?.sections;

        if (sections && sections.length > 0) {
          const section = sections[sectionIndex];
          if (section) {
            const field = section.fields.find(field => field.tag === fieldTag);
            if (field) {
              field.width = fieldWidth;
            } else {
              // eslint-disable-next-line no-console
              console.error(JSON.stringify(dataObject, null, 2));
              throw new Error(
                `Field ${fieldTag} not found in section ${sectionIndex} in record ${dataObjectTag}`
              );
            }
          } else {
            // eslint-disable-next-line no-console
            console.error(JSON.stringify(dataObject, null, 2));
            throw new Error(
              `Section ${sectionIndex} not found in record ${dataObjectTag}`
            );
          }
        } else {
          const sections = getDefaultSections(dataObject.schema.properties);
          if (sectionIndex === 0) {
            sections[0].fields.push({
              tag: fieldTag,
              width: fieldWidth
            });
          } else {
            // eslint-disable-next-line no-console
            console.error(JSON.stringify(dataObject, null, 2));
            throw new Error(
              `Section ${sectionIndex} not found in record ${dataObjectTag}`
            );
          }
          dataObject.uiSchema = {
            ...dataObject.uiSchema,
            detail: {
              sections
            }
          };
        }
        dbOps().setDataObject(dataObject.tag, dataObject);
      } else {
        throw new Error(`Record ${dataObjectTag} not found`);
      }
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type ChangeFiledPositionParams = {
  dataObjectTag: string;
  oldSectionIndex: number;
  newSectionIndex: number;
  oldFieldIndex: number;
  newFieldIndex: number;
};

export const changeFiledPosition = (params: ChangeFiledPositionParams) => {
  const {
    dataObjectTag,
    oldSectionIndex,
    newSectionIndex,
    oldFieldIndex,
    newFieldIndex
  } = params;

  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (!dataObject) return;

      const sections = dataObject.uiSchema.detail?.sections;
      if (!sections) return;

      const sourceSection = sections[oldSectionIndex];
      const targetSection = sections[newSectionIndex];

      if (!sourceSection || !targetSection) return;

      // Find and remove field from source section
      const [field] = sourceSection.fields.splice(oldFieldIndex, 1);

      // Add field to target section at target index
      targetSection.fields.splice(newFieldIndex, 0, field);

      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type UpdateFieldTitleParams = {
  dataObjectTag: string;
  fieldTag: string;
  title: string;
};

export const updateFieldTitle = (params: UpdateFieldTitleParams) => {
  const { dataObjectTag, fieldTag, title } = params;
  dbOps().transaction(() => {
    const dataObject = dbOps().getDataObject(dataObjectTag);
    if (dataObject) {
      const field = dataObject.uiSchema.fields?.[fieldTag];
      if (field) {
        field.title = title;
      } else {
        const fieldSchema = dataObject.schema.properties?.[fieldTag];
        if (fieldSchema) {
          const fields = dataObject.uiSchema.fields;
          if (fields) {
            fields[fieldTag] = {
              type: fieldSchema.type as DataObjectUISchemaFieldTypes, // TODO: Can we make schema'field and Ui schema field types same?,
              title: title,
              format: fieldSchema.format
            };
          } else {
            dataObject.uiSchema.fields = {
              [fieldTag]: {
                type: fieldSchema.type as DataObjectUISchemaFieldTypes, // TODO: Can we make schema'field and Ui schema field types same?,
                title: title,
                format: fieldSchema.format
              }
            };
          }
        } else {
          // eslint-disable-next-line no-console
          console.error(JSON.stringify(dataObject, null, 2));
          throw new Error(
            `Field schema ${fieldTag} not found in record ${dataObjectTag}`
          );
        }
      }
      dbOps().setDataObject(dataObject.tag, dataObject);
    } else {
      throw new Error(`Record ${dataObjectTag} not found`);
    }
  });
};

type UpdateSectionTitleParams = {
  dataObjectTag: string;
  sectionIndex: number;
  title: string;
};

export const updateSectionTitle = (params: UpdateSectionTitleParams) => {
  const { dataObjectTag, sectionIndex, title } = params;
  dbOps().transaction(() => {
    const dataObject = dbOps().getDataObject(dataObjectTag);
    if (dataObject) {
      const section = dataObject.uiSchema.detail?.sections?.[sectionIndex];
      if (section) {
        section.title = title;
      } else if (
        sectionIndex === 0 &&
        !dataObject.uiSchema.detail?.sections?.length
      ) {
        const sections = getDefaultSections(dataObject.schema.properties);
        sections[0].title = title;

        dataObject.uiSchema.detail = {
          ...dataObject.uiSchema.detail,
          sections
        };
      } else {
        // eslint-disable-next-line no-console
        console.error(JSON.stringify(dataObject, null, 2));
        throw new Error(
          `Section ${sectionIndex} not found in record ${dataObjectTag}`
        );
      }
      dbOps().setDataObject(dataObject.tag, dataObject);
    } else {
      throw new Error(`Record ${dataObjectTag} not found`);
    }
  });
};

type DeleteFieldFromDataObjectParams = {
  dataObjectTag: string;
  sectionIndex: number;
  fieldTag: string;
};

export const deleteFieldFromDataObject = (
  params: DeleteFieldFromDataObjectParams
) => {
  const { dataObjectTag, sectionIndex, fieldTag } = params;
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (!dataObject) return;

      // Remove from schema.properties
      delete dataObject.schema.properties[fieldTag];

      // Remove from schema.required if present
      if (dataObject.schema.required) {
        dataObject.schema.required = dataObject.schema.required.filter(
          tag => tag !== fieldTag
        );
      }

      // Remove from uiSchema.fields
      if (dataObject.uiSchema.fields) {
        delete dataObject.uiSchema.fields[fieldTag];
      }

      // Remove from section fields
      const sections = dataObject.uiSchema.detail?.sections;
      if (sections && sections[sectionIndex]) {
        const section = sections[sectionIndex];
        section.fields = section.fields.filter(field => field.tag !== fieldTag);
      }

      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type DuplicateFieldInDataObjectParams = {
  dataObjectTag: string;
  sectionIndex: number;
  fieldTag: string;
};

export const duplicateFieldInDataObject = (
  params: DuplicateFieldInDataObjectParams
) => {
  const { dataObjectTag, sectionIndex, fieldTag } = params;
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (!dataObject) return;

      const field = dataObject.schema.properties[fieldTag];
      const isFieldRequired = dataObject.schema.required?.includes(fieldTag);
      const fieldUISchema = dataObject.uiSchema.fields?.[fieldTag];

      if (!field) return;

      // Generate new unique field tag
      let newFieldTag = `${fieldTag}_copy`;
      let counter = 1;
      while (dataObject.schema.properties[newFieldTag]) {
        newFieldTag = `${fieldTag}_copy_${counter}`;
        counter++;
      }

      // Duplicate schema field
      dataObject.schema.properties[newFieldTag] = {
        ...field,
        __isFrozen: false
      };

      // Duplicate required fields
      if (isFieldRequired && dataObject.schema.required) {
        dataObject.schema.required.push(newFieldTag);
      }

      // Duplicate uiSchema field
      if (fieldUISchema && dataObject.uiSchema.fields) {
        dataObject.uiSchema.fields[newFieldTag] = { ...fieldUISchema };
      }

      // Add to section after the original field
      const sections = dataObject.uiSchema.detail?.sections;
      if (sections && sections[sectionIndex]) {
        const section = sections[sectionIndex];
        const originalFieldIndex = section.fields.findIndex(
          f => f.tag === fieldTag
        );
        if (originalFieldIndex !== -1) {
          const originalFieldWidth = section.fields[originalFieldIndex].width;
          section.fields.splice(originalFieldIndex + 1, 0, {
            tag: newFieldTag,
            width: originalFieldWidth || "1/2"
          });
        } else {
          section.fields.push({
            tag: newFieldTag,
            width: "1/2"
          });
        }
      }

      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type UpdateFieldRequiredParams = {
  dataObjectTag: string;
  fieldTag: string;
  isRequired: boolean;
};

export const updateFieldRequired = (params: UpdateFieldRequiredParams) => {
  const { dataObjectTag, fieldTag, isRequired } = params;
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (!dataObject) return;

      if (!dataObject.schema.required) {
        dataObject.schema.required = [];
      }

      if (isRequired) {
        // Add to required if not already present
        if (!dataObject.schema.required.includes(fieldTag)) {
          dataObject.schema.required.push(fieldTag);
        }
      } else {
        // Remove from required
        dataObject.schema.required = dataObject.schema.required.filter(
          tag => tag !== fieldTag
        );
      }

      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type UpdateFieldFormatParams = {
  dataObjectTag: string;
  fieldTag: string;
  format: string | undefined;
};

export const updateFieldFormat = (params: UpdateFieldFormatParams) => {
  const { dataObjectTag, fieldTag, format } = params;
  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (!dataObject) return;

      // Update schema format
      const field = dataObject.schema.properties[fieldTag];
      if (field) {
        if (format) {
          field.format = format;
        } else {
          delete field.format;
        }
      }

      // Update uiSchema format
      if (dataObject.uiSchema.fields?.[fieldTag]) {
        if (format) {
          dataObject.uiSchema.fields[fieldTag].format = format;
        } else {
          delete dataObject.uiSchema.fields[fieldTag].format;
        }
      }

      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

type ChangeSectionPositionParams = {
  dataObjectTag: string;
  oldSectionIndex: number;
  newSectionIndex: number;
};

export const changeSectionPosition = (params: ChangeSectionPositionParams) => {
  const { dataObjectTag, oldSectionIndex, newSectionIndex } = params;

  dbOps().transaction(
    () => {
      const dataObject = dbOps().getDataObject(dataObjectTag);
      if (!dataObject) return;

      const sections = dataObject.uiSchema.detail?.sections;
      if (!sections) return;

      // Remove section from old position
      const [section] = sections.splice(oldSectionIndex, 1);

      // Insert section at new position
      sections.splice(newSectionIndex, 0, section);

      dbOps().setDataObject(dataObject.tag, dataObject);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECT
    }
  );
};

export const deleteDataObject = (objectTag: string) => {
  dbOps().transaction(
    () => {
      dbOps().removeDataObject(objectTag);
    },
    {
      intent: Intents.DELETE_DATA_OBJECT
    }
  );
};

export const replaceDataObjects = (
  dataObjects: Record<string, DataObjectType>
) => {
  dbOps().transaction(
    () => {
      dbOps().replaceDataObjects(dataObjects);
    },
    {
      intent: Intents.UPDATE_DATA_OBJECTS,
      skipLoggingInLedgerAndUndoStack: true
    }
  );
};
